import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Calendar, Trash2, Clock, Edit2, Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCalendar } from '../../contexts/CalendarContext';
import CreateCalendarModal from './CreateCalendarModal';
import DeleteModal from '../Common/DeleteModal';
import ContextMenu from '../Common/ContextMenu';
import { format } from 'date-fns';
import { getDateFnsLocale } from '../../utils/dateLocale';
const CalendarSidebar = () => {
  const { t, i18n } = useTranslation('calendar');
  const dateLocale = getDateFnsLocale(i18n.language);
  const {
    calendars,
    selectedCalendar,
    createCalendar,
    deleteCalendar,
    selectCalendar,
    getEventsForDate,
    events,
    updateCalendar,
    duplicateCalendar,
  } = useCalendar();
  const [modalState, setModalState] = useState({ isOpen: false, mode: 'create', calendar: null });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, calendarId: null });
  const [contextMenuState, setContextMenuState] = useState({
    isOpen: false,
    position: { x: 0, y: 0 },
    calendar: null,
  });
  const [calendarSearchQuery, setCalendarSearchQuery] = useState('');

  // Takvim listesini arama metnine göre filtrele
  const filteredCalendars = useMemo(() => {
    const q = (calendarSearchQuery || '').trim().toLowerCase();
    if (!q) return calendars;
    return calendars.filter(
      (cal) =>
        (cal.name && cal.name.toLowerCase().includes(q)) ||
        (cal.description && cal.description.toLowerCase().includes(q))
    );
  }, [calendars, calendarSearchQuery]);

  // Bugünün etkinliklerini al (id'ye göre tekilleştirilmiş)
  const todayEvents = useMemo(() => {
    const today = new Date();
    const allTodayEvents = [];
    const seenIds = new Set();

    calendars.forEach(calendar => {
      const dayEvents = getEventsForDate(today, calendar.id);
      dayEvents.forEach(event => {
        if (seenIds.has(event.id)) return;
        seenIds.add(event.id);
        allTodayEvents.push({ ...event, calendar });
      });
    });

    return allTodayEvents.sort((a, b) => {
      if (a.allDay && !b.allDay) return -1;
      if (!a.allDay && b.allDay) return 1;
      if (a.allDay && b.allDay) return 0;
      return (a.startTime || '').localeCompare(b.startTime || '');
    });
  }, [calendars, events, getEventsForDate]);

  const openCreateModal = () => {
    setModalState({ isOpen: true, mode: 'create', calendar: null });
  };

  const openEditModal = (calendar) => {
    setModalState({ isOpen: true, mode: 'edit', calendar });
  };

  const handleCreateCalendar = async (calendarData) => {
    try {
      const newCalendar = await createCalendar(calendarData);
      if (newCalendar) {
        selectCalendar(newCalendar);
      }
    } catch (error) {
      console.error('Takvim oluşturulurken hata oluştu:', error);
    }
  };

  const handleUpdateCalendar = async (calendarData) => {
    if (!modalState.calendar) return;
    const updatedCalendar = { ...modalState.calendar, ...calendarData };
    await updateCalendar(updatedCalendar);
    selectCalendar(updatedCalendar);
  };

  const handleDeleteClick = (calendarId, e) => {
    e.stopPropagation();
    setDeleteModal({ isOpen: true, calendarId });
  };

  const handleDeleteConfirm = () => {
    if (deleteModal.calendarId) {
      deleteCalendar(deleteModal.calendarId);
      if (selectedCalendar?.id === deleteModal.calendarId) {
        selectCalendar(null);
      }
    }
    setDeleteModal({ isOpen: false, calendarId: null });
  };

  const handleContextMenu = (event, calendar) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenuState({
      isOpen: true,
      position: { x: event.clientX, y: event.clientY },
      calendar,
    });
  };

  const closeContextMenu = () => {
    setContextMenuState((prev) => ({ ...prev, isOpen: false, calendar: null }));
  };

  const handleDuplicateCalendarClick = async (calendar) => {
    const result = await duplicateCalendar(calendar.id);
    if (result?.calendar) {
      selectCalendar(result.calendar);
    }
  };

  const contextMenuItems = contextMenuState.calendar
    ? [
        {
          label: t('sidebar.contextMenu.edit'),
          icon: Edit2,
          onClick: () => {
            if (!contextMenuState.calendar) return;
            openEditModal(contextMenuState.calendar);
          },
        },
        {
          label: t('sidebar.contextMenu.duplicate'),
          icon: Copy,
          onClick: () => {
            if (!contextMenuState.calendar) return;
            handleDuplicateCalendarClick(contextMenuState.calendar);
          },
        },
        {
          label: t('sidebar.contextMenu.delete'),
          icon: Trash2,
          onClick: () => {
            if (!contextMenuState.calendar) return;
            setDeleteModal({ isOpen: true, calendarId: contextMenuState.calendar.id });
          },
          danger: true,
        },
      ]
    : [];

  return (
    <div className="flex flex-col h-full overflow-hidden bg-transparent relative">
      {/* Search Header - h-16 to align with main Header */}
      <div className="h-16 flex items-center px-4 border-b border-slate-200 dark:border-white/5 shrink-0">
        <div className="relative w-full">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 material-symbols-outlined" style={{ fontSize: '20px' }}>search</span>
          <input
            type="text"
            placeholder={t('sidebar.searchPlaceholder')}
            value={calendarSearchQuery}
            onChange={(e) => setCalendarSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-slate-100 dark:bg-[var(--color-bg-base)] border-none rounded-lg text-sm text-slate-900 dark:text-slate-200 placeholder-slate-500 dark:placeholder-slate-500 focus:ring-2 focus:ring-primary/50"
          />
        </div>
      </div>

      {/* Calendars List */}
      <div className="flex-1 overflow-y-auto p-3 min-h-0 relative pb-24">
        <AnimatePresence>
          {calendars.length === 0 ? (
            <div className="text-center text-gray-500 dark:text-[var(--color-text-secondary)] text-sm py-12">
              <div className="flex flex-col items-center space-y-2">
                <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-[var(--color-bg-elevated)] flex items-center justify-center border border-gray-200 dark:border-white/10">
                  <Calendar className="w-6 h-6 text-gray-400 dark:text-[var(--color-text-secondary)]" />
                </div>
                <p>{t('sidebar.noCalendarsYet')}</p>
                <p className="text-xs text-gray-500 dark:text-[var(--color-text-secondary)]">{t('sidebar.noCalendarsHint')}</p>
              </div>
            </div>
          ) : filteredCalendars.length === 0 ? (
            <div className="text-center text-gray-500 dark:text-[var(--color-text-secondary)] text-sm py-8">
              <p>{t('sidebar.noMatchingCalendars')}</p>
              <p className="text-xs mt-1">{t('sidebar.noMatchingCalendarsHint')}</p>
            </div>
          ) : (
            filteredCalendars.map((calendar) => (
              <motion.div
                key={calendar.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
                className={`mb-2.5 p-3.5 rounded-xl cursor-pointer transition-all duration-200 group ${
                  selectedCalendar?.id === calendar.id
                    ? 'bg-[#2563eb]/15 dark:bg-blue-500/20 border border-[#3d8ef0]/50 dark:border-blue-500/50'
                    : 'bg-white dark:bg-[var(--color-bg-elevated)] hover:bg-gray-50 dark:hover:bg-[#24354f] border border-slate-200 dark:border-white/10 hover:shadow-md'
                }`}
                onClick={() => selectCalendar(calendar)}
                onContextMenu={(event) => handleContextMenu(event, calendar)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div
                      className="w-4 h-4 rounded-full flex-shrink-0 mt-1"
                      style={{ backgroundColor: calendar.color }}
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 dark:text-white truncate mb-1 text-sm leading-tight">
                        {calendar.name}
                      </h3>
                      {calendar.description && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 leading-relaxed">
                          {calendar.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex-shrink-0">
                    <motion.button
                      whileHover={{ scale: 1.1, backgroundColor: 'rgba(239, 68, 68, 0.2)' }}
                      whileTap={{ scale: 0.9 }}
                      onClick={(e) => handleDeleteClick(calendar.id, e)}
                      className="p-1.5 rounded-lg hover:bg-red-500/20 text-gray-500 dark:text-[var(--color-text-secondary)] hover:text-red-500 dark:hover:text-red-400 transition-all duration-200"
                      title={t('sidebar.delete')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </div>

      {/* Bugünün Etkinlikleri */}
      {todayEvents.length > 0 && (
        <div className="border-t border-slate-200 dark:border-white/10 p-3 flex-shrink-0 bg-gray-50/70 dark:bg-[var(--color-bg-elevated)]">
          <div className="flex items-center gap-2 mb-2.5">
            <div className="w-1.5 h-1.5 rounded-full bg-slate-300 dark:bg-[#24354f] border border-slate-200 dark:border-white/10"></div>
            <h4 className="text-xs font-semibold text-gray-700 dark:text-[var(--color-text-secondary)] uppercase tracking-wider">
              {t('sidebar.todayLabel', { date: format(new Date(), 'd MMM', { locale: dateLocale }) })}
            </h4>
          </div>
          <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
            <AnimatePresence>
              {todayEvents.map((event, idx) => (
                <motion.div
                  key={`${event.id}-${idx}`}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  transition={{ duration: 0.2 }}
                  className="p-2 rounded-lg border transition-all duration-200 group bg-white dark:bg-[var(--color-bg-elevated)] hover:bg-gray-50 dark:hover:bg-[#24354f] border-slate-200 dark:border-white/10"
                >
                  <div className="flex items-start gap-2">
                    <div
                      className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5"
                      style={{ backgroundColor: event.calendar?.color || '#ffffff' }}
                    />
                    <div className="flex-1 min-w-0">
                      <h5 className="text-xs font-semibold text-gray-900 dark:text-white truncate mb-0.5">
                        {event.title}
                      </h5>
                      {!event.allDay && event.startTime && (
                        <div className="flex items-center gap-1 text-[10px] text-gray-600 dark:text-[var(--color-text-secondary)]">
                          <Clock className="w-3 h-3" strokeWidth={2} />
                          <span>{event.startTime}</span>
                          {event.endTime && <span> - {event.endTime}</span>}
                        </div>
                      )}
                      {event.allDay && (
                        <div className="text-[10px] text-gray-600 dark:text-[var(--color-text-secondary)]">
                          {t('sidebar.allDay')}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* Floating Action Button (FAB) - same position as Notes */}
      <motion.button
        type="button"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={openCreateModal}
        className="absolute bottom-6 right-6 w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-500/30 flex items-center justify-center text-white cursor-pointer z-50 transition-transform hover:scale-105"
        title={t('sidebar.createNewCalendar')}
      >
        <Plus className="w-6 h-6" strokeWidth={2.5} />
      </motion.button>

      {/* Create Modal */}
      <CreateCalendarModal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ isOpen: false, mode: 'create', calendar: null })}
        mode={modalState.mode}
        initialCalendar={modalState.calendar}
        onCreate={handleCreateCalendar}
        onUpdate={handleUpdateCalendar}
      />

      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, calendarId: null })}
        onConfirm={handleDeleteConfirm}
        title={t('sidebar.deleteModal.title')}
        message={t('sidebar.deleteModal.message')}
      />
      <ContextMenu
        isOpen={contextMenuState.isOpen}
        position={contextMenuState.position}
        items={contextMenuItems}
        onClose={closeContextMenu}
      />
    </div>
  );
};

export default CalendarSidebar;

